import type { ReactNode } from 'react';

import { useFeatures } from '@/shared/session/currentUser';
import { Card } from '@/shared/ui/Card';
import { PageHeader } from '@/shared/ui/PageHeader';

/**
 * What the AI does here, what it does not, what it sees and who handles it, in plain
 * words. The information the AI Act asks for (Reglamento (UE) 2024/1689, artículo 50)
 * and the addendum promises (cláusula 8.3), written for the people who use the tool,
 * not for a lawyer. The same text goes in `docs/formacion.md` and `docs/para-cubas.md`
 * of the back, so the training session and the printed sheet say the same thing.
 *
 * The content is the point of the page and does not get shortened: it is the legal
 * information. What the layout does is keep it readable, two columns from 1024 px, one
 * on a phone.
 */
function Points({ children }: { children: ReactNode }) {
  return (
    <ul className="text-ink-2 flex list-disc flex-col gap-2 pl-5 text-[14px] leading-relaxed">
      {children}
    </ul>
  );
}

export function AboutPage() {
  const { document_archive: documentArchive, payroll } = useFeatures();
  return (
    <>
      <PageHeader
        title="Qué hace la inteligencia artificial aquí, y qué no"
        description="Esta herramienta usa inteligencia artificial para proponer. Las decisiones las toma siempre una persona."
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Qué hace la IA">
          <Points>
            <li>
              <strong>Lee cada correo que entra</strong> en los buzones conectados (asunto, cuerpo y
              adjuntos que pueda abrir) y propone qué es: una factura, un pedido, una oferta de
              proveedor, un aviso del banco…
            </li>
            <li>
              <strong>Propone a quién le toca</strong> ese correo según las reglas de la casa
              (administración, María, Paulo, o que se quede donde está).
            </li>
            <li>
              <strong>Lee las ofertas de proveedores</strong> (PDF, foto, Excel, Word o el cuerpo
              del correo) y saca las líneas: referencia, descripción, cantidad, precio, descuentos y
              los avisos que el proveedor escribe.
            </li>
            <li>
              Con esas líneas <strong>prepara un borrador de presupuesto</strong> con el margen por
              defecto aplicado.
            </li>
            <li>
              <strong>Ayuda a escribir un correo</strong> cuando alguien pulsa «Ayuda a redactar con
              IA»: lo redacta a partir de lo que esa persona explica, o repasa lo que ya ha escrito
              (ortografía, acortar, más formal, más cercano). Enseña su versión al lado de la de la
              persona, y la persona elige cuál se queda.
            </li>
          </Points>
        </Card>

        <Card title="Qué no hace">
          <Points>
            <li>
              <strong>No envía presupuestos a ningún cliente.</strong> Los envía una persona, desde
              su correo, cuando ha revisado el PDF. Tampoco envía ni guarda un correo que haya
              redactado: lo que propone se queda en la ventana de escribir hasta que la persona lo
              envía.
            </li>
            <li>
              <strong>No reenvía ni mueve correo por su cuenta</strong> mientras esté en la fase
              inicial: propone, y el reenvío se hace cuando alguien pulsa «Correcto» o «Corregir
              a…». Pasar una categoría a automático es una decisión escrita de Cubas.
            </li>
            <li>
              <strong>No borra correo</strong>, nunca. Un correo cambia de carpeta cuando alguien
              pulsa «Correcto» o «Corregir…», y «Eliminar» lo lleva a la papelera del propio buzón.
            </li>
            <li>
              <strong>No bloquea a nadie.</strong> Un correo del que no está segura se queda donde
              está y se marca como duda.
            </li>
            <li>
              <strong>Google no aprende de vuestros correos.</strong> No usa lo que se le manda para
              entrenar sus modelos (condiciones de servicio para empresas). Lo que sí aprende la
              aplicación, aquí, es de vuestras correcciones y de las notas que escribís al corregir:
              cada nota se convierte en un criterio que vale desde el siguiente correo, se ve debajo
              de la corrección y se puede deshacer. Cada buzón aprende solo de lo suyo.
            </li>
            <li>
              <strong>Se puede equivocar.</strong> Una etiqueta o una línea de presupuesto mal leída
              no es raro. Por eso todo lo que propone lleva el aviso «propuesta generada por
              inteligencia artificial» y una persona lo revisa antes de que tenga efecto.
            </li>
          </Points>
        </Card>

        <Card title="Qué datos ve">
          <Points>
            <li>
              Para clasificar:{' '}
              <strong>el remitente, el asunto, el cuerpo del correo y los adjuntos legibles</strong>{' '}
              (PDF, hojas de cálculo, Word y fotos de más de 100 KB). Un logo o una firma no se
              envían. Y lo aprendido de ese remitente en el mismo buzón:{' '}
              <strong>los criterios sacados de vuestras notas</strong> y los últimos correos suyos
              ya respondidos (asunto, un trozo del cuerpo, nombres de los adjuntos y la nota).
            </li>
            <li>
              Para aprender de una nota: <strong>la nota</strong>, lo que se propuso y lo que
              dejasteis, y el texto del correo en el que la escribisteis.
            </li>
            <li>
              Para leer una oferta: <strong>el documento de la oferta</strong> y el texto del correo
              que la trae.
            </li>
            <li>
              Para ayudar a escribir un correo: <strong>lo que explica la persona</strong> y, si
              responde a una conversación, sus últimos mensajes (solo lo que dice cada uno, sin lo
              que cita, y los nombres de los adjuntos), además de hasta tres correos suyos de antes
              desde ese buzón, para escribir como ella. Para repasar, solo lo que ha escrito. Nunca
              el contenido de un adjunto, la firma ni las direcciones en copia oculta. La propuesta
              se borra a los diez minutos; solo queda lo que costó.
            </li>
            <li>
              Para cargar una tarifa de proveedor, y solo si no reconoce sus columnas:{' '}
              <strong>los títulos de las columnas y veinte filas</strong>, para proponer cuál es
              cuál. Las filas las lee la plataforma sin la inteligencia artificial, y una persona
              confirma las columnas antes de leerlas.
            </li>
            {payroll ? (
              <li>
                <strong>La inteligencia artificial nunca ve lo que dice una nómina.</strong> Las
                separa por persona la plataforma, mirando el NIF y el nombre, y lo que llega al
                buzón de las nóminas no se clasifica. Solo cuando el correo de la asesoría no dice
                «nómina», la inteligencia artificial decide si son las nóminas viendo el asunto, el
                remitente y los nombres de los adjuntos. Sus ficheros se borran a los 30 días.
              </li>
            ) : null}
            <li>
              Los correos, con su texto, sus adjuntos y la copia del mensaje,{' '}
              <strong>se guardan mientras dure el servicio</strong>, porque hacen falta para
              trabajar con ellos; y con ellos lo que se decidió, vuestras correcciones y sus notas.
              Lo aprendido de una nota se guarda mientras vale; el que deja de valer se borra a los
              90 días.
              {documentArchive ? (
                <>
                  {' '}
                  Los documentos archivados siguen además las reglas de conservación del archivo
                  documental (facturas y albaranes, seis años como mínimo, por obligación legal).
                </>
              ) : null}
            </li>
            <li>
              Cada acceso a la aplicación queda registrado (quién entra, cuándo, qué cambia), y ese
              registro se conserva doce meses.
            </li>
          </Points>
        </Card>

        <Card title="Quién trata los datos">
          <Points>
            <li>
              <strong>Ferretería Cubas</strong> es la responsable de los datos.{' '}
              <strong>Ignira</strong> los trata por encargo, según el acuerdo firmado con la adenda
              del contrato.
            </li>
            <li>
              <strong>Hetzner Online GmbH</strong> (Alemania, Unión Europea) aloja la aplicación, la
              base de datos, los adjuntos y las copias de seguridad.
            </li>
            <li>
              <strong>Google Ireland Limited</strong> presta la inteligencia artificial (Gemini). Se
              le envía cada correo para clasificarlo, cada oferta para leerla, cada nota de una
              corrección para convertirla en criterio, el principio de una tarifa cuyas columnas no
              reconoce y lo que se le pide al escribir un correo; los documentos subidos se borran
              de su lado en cuanto termina la lectura. El tratamiento se hace en la región de la
              Unión Europea de Vertex AI.
            </li>
            <li>
              Nadie más. Los avisos de error que recibe Ignira (Sentry) no llevan contenido de
              correos ni datos de personas.
            </li>
          </Points>
          <p className="text-ink-3 mt-1 text-[13px] leading-relaxed">
            Base legal: Reglamento (UE) 2024/1689 (inteligencia artificial), artículo 50, y
            Reglamento (UE) 2016/679 (protección de datos), artículo 28. Esta herramienta no es un
            sistema de alto riesgo según ese reglamento. Dudas: Ignira,{' '}
            <a href="mailto:info@igniratech.es" className="link">
              info@igniratech.es
            </a>
            .
          </p>
        </Card>
      </div>
    </>
  );
}
